import { ChevronLeft, VideoOff } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button, TextField } from '@krp/ui-kit'

import type { ScannerActions } from '../../model/types'
import { createBarcodeDecoder, type BarcodeDecoderFactory } from './barcodeDecoder'
import { useCameraStream } from './useCameraStream'
import './scanner.css'

/** Тот же код подряд не перечитываем чаще: кадр за кадром он один и тот же. */
const SAME_CODE_COOLDOWN_MS = 2000

/**
 * Сканер окна продажи — окно `size: 'screen'` (карточка 267): поверхность,
 * адрес и закрытие даёт оболочка окна. Код со штрихкода уходит командой в
 * пакет; товар в каталоге кабинета ищет listener, итог — строкой внизу.
 * Камеры нет — тот же путь вводом кода руками.
 */
export function ScannerScreen({
  message,
  actions,
  decoderFactory = createBarcodeDecoder,
}: {
  /** Итог последнего кода: «Добавлено: …» или почему не добавлено. */
  message: string | null
  actions: ScannerActions
  decoderFactory?: BarcodeDecoderFactory
}) {
  const video = useRef<HTMLVideoElement>(null)
  const status = useCameraStream(video)
  const lastSeen = useRef({ code: '', at: 0 })
  const [typed, setTyped] = useState('')
  const { code: send, close } = actions

  const onCode = useCallback(
    (raw: string) => {
      const now = Date.now()
      if (raw === lastSeen.current.code && now - lastSeen.current.at < SAME_CODE_COOLDOWN_MS) return
      lastSeen.current = { code: raw, at: now }
      send(raw)
    },
    [send],
  )

  useEffect(() => {
    const element = video.current
    if (status !== 'live' || element === null) return undefined
    const decoder = decoderFactory()
    decoder.start(element, onCode)
    return () => decoder.stop()
  }, [decoderFactory, onCode, status])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [close])

  const cameraProblem = status === 'denied' || status === 'unavailable'
  return (
    <div className="aqsi-scanner">
      <div className="aqsi-scanner__bar">
        <button type="button" className="aqsi-scanner__back" onClick={close} aria-label="Назад к продаже">
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <span className="aqsi-scanner__title">Скан товара</span>
        <span className="aqsi-scanner__live" data-status={status}>
          {status === 'live' ? '● камера' : status === 'starting' ? '○ запуск' : '× нет камеры'}
        </span>
      </div>
      <div className="aqsi-scanner__view">
        {/* playsInline + muted — иначе iOS открывает поток на весь экран и не даёт кадры. */}
        <video ref={video} className="aqsi-scanner__video" playsInline muted autoPlay />
        {cameraProblem ? (
          <div className="aqsi-scanner__problem" role="alert">
            <VideoOff size={24} aria-hidden="true" />
            <p>
              {status === 'denied'
                ? 'Доступ к камере запрещён. Разрешите его в настройках браузера для этого сайта — или введите код ниже.'
                : 'Браузер не даёт поток камеры (нужен HTTPS и разрешение). Введите код ниже.'}
            </p>
          </div>
        ) : (
          <p className="aqsi-scanner__hint">Наведите камеру на штрихкод</p>
        )}
      </div>
      <form
        className="aqsi-scanner__manual"
        onSubmit={(event) => {
          event.preventDefault()
          if (typed.trim() !== '') send(typed.trim())
          setTyped('')
        }}
      >
        <TextField label="Штрихкод вручную" value={typed} onChange={setTyped} />
        <Button type="submit">Добавить</Button>
      </form>
      <p className="aqsi-scanner__status" role="status" data-testid="aqsi-scanner-status">
        {message ?? ' '}
      </p>
    </div>
  )
}
